import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import { SplashScreen } from '@capacitor/splash-screen'
import { SafeArea } from 'capacitor-plugin-safe-area'
import { createApp } from 'vue'

import { LDialogPlugin, LNotificationPlugin, trackerPlugin } from '@last/core-ui/paprika'
import { vHorizontalScroll } from '@last/core-ui/paprika/directives'

import AppRoot from '@/App.vue'
import i18n from '@/i18n'
import localDb from '@/localDb'
import { ManagerConfirmationPlugin } from '@/plugins/managerConfirmation'
import { router } from '@/router'
import { useAuthStore } from '@/store/auth'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useCouriersStore } from '@/store/couriers'
import { pinia } from '@/store/pinia'
import { usePromotionsStore } from '@/store/promotions'
import { useStatusStore } from '@/store/status'
import { useTabsStore } from '@/store/tabs'
import sync from '@/sync/service'
import { tracker } from '@/tracker'

import { rum } from './monitoring'
import { CashMachineStatusPlugin } from './plugins/cashMachineStatus'
import { UpdateModalPlugin } from './plugins/updateVersion'
import UpdaterEngine from './updater'

import './style.css'

if (Capacitor.getPlatform() === 'ios') {
  Keyboard.setResizeMode({ mode: KeyboardResize.Native })
}

const app = createApp(AppRoot)

app.config.performance = import.meta.env.DEV

app.directive('horizontal-scroll', vHorizontalScroll)
app.use(pinia)
app.use(i18n)
app.use(router)
app.use(ManagerConfirmationPlugin)
app.use(CashMachineStatusPlugin)
app.use(UpdateModalPlugin)
app.use(LDialogPlugin)
app.use(LNotificationPlugin)

rum.setRouter(router)
if (import.meta.env.PROD) {
  app.config.errorHandler = err => {
    rum.addError(err)
  }
}

app.use(trackerPlugin, { tracker })

app.mount('#app')

async function init() {
  if (window.location.href.includes('support-login')) {
    return localStorage.clear()
  }

  UpdaterEngine.notifyAppReady()

  const authStore = useAuthStore(pinia)
  const configStore = useConfigStore(pinia)
  const tabsStore = useTabsStore(pinia)
  const catalogStore = useCatalogStore(pinia)
  const statusStore = useStatusStore(pinia)
  const promotionsStore = usePromotionsStore(pinia)
  const couriersStore = useCouriersStore(pinia)

  if (!sync.isInitialized()) {
    const tabKeys = Object.keys(tabsStore).map(key => `tabs:${key}`)
    localDb.loadTables(tabKeys).then(tabsTables => {
      tabsTables = Object.keys(tabsTables).reduce((res, key) => {
        res[key.replace('tabs:', '')] = tabsTables[key]
        return res
      }, {} as any)
      if (!sync.isInitialized()) {
        // Avoid race condition with sync
        tabsStore.replaceTabs(tabsTables)
      }
    })
  }

  if (authStore.isAuthenticated) {
    await statusStore.performInitialSync()
  }

  CapacitorApp.addListener('appStateChange', state => {
    if (state.isActive && authStore.isAuthenticated) {
      sync.resync()
      configStore.refreshConfig()
      catalogStore.refreshCatalogs()
      authStore.refreshEmployees()
      statusStore.refreshErrorMessages()
      promotionsStore.refreshPromotions()
      couriersStore.refreshCouriers()
    }
  })

  SplashScreen.hide()
}

/**
 * We use immersive mode and we need to set the safe area insets to the root element.
 * The keyboard on Android also changes the safe area insets, so we need to update them when the keyboard is shown or hidden.
 */
async function setupSafeArea() {
  if (!['android', 'ios'].includes(Capacitor.getPlatform())) return

  const updateProperties = async () => {
    const { insets } = await SafeArea.getSafeAreaInsets()
    for (const [key, value] of Object.entries(insets)) {
      document.documentElement.style.setProperty(
        `--safe-area-inset-${key}`,
        `max(env(safe-area-inset-${key}), ${value}px)`
      )
    }
  }

  updateProperties()
  SafeArea.addListener('safeAreaChanged', () => updateProperties())
  Keyboard.addListener('keyboardWillShow', () => {
    updateProperties()
    document.documentElement.classList.add('keyboard-shown')
  })
  Keyboard.addListener('keyboardWillHide', () => {
    updateProperties()
    document.documentElement.classList.remove('keyboard-shown')
  })
}

setupSafeArea()
init()
